import styled from 'styled-components';
import { designTokens } from '../foundation/DesignTokens';

// Modal Overlay - Full screen backdrop
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow-y: auto;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

// Modal Container - The white box
export const ModalContainer = styled.div`
  background: white;
  border-radius: ${designTokens.radius.xl};
  box-shadow: ${designTokens.shadows.xl};
  max-width: ${props => {
    switch(props.size) {
      case 'sm': return '400px';
      case 'md': return '600px';
      case 'lg': return '800px';
      case 'xl': return '1000px';
      case 'full': return '95vw';
      default: return '600px';
    }
  }};
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  position: relative;
  animation: slideUp 0.25s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 95vh;
    border-radius: ${designTokens.radius.lg} ${designTokens.radius.lg} 0 0;
    margin-top: auto;
  }
`;

// Modal Header
export const ModalHeader = styled.div`
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid ${designTokens.colors.gray[200]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 20px 16px 12px 16px;
  }
`;

// Modal Title
export const ModalTitle = styled.h2`
  font-family: ${designTokens.fonts.heading};
  font-size: 1.25rem;
  font-weight: 700;
  color: ${designTokens.colors.gray[900]};
  margin: 0;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

// Modal Close Button
export const ModalCloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: ${designTokens.colors.gray[100]};
  color: ${designTokens.colors.gray[600]};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: ${designTokens.colors.gray[200]};
    color: ${designTokens.colors.gray[900]};
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

// Modal Body
export const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${designTokens.colors.gray[100]};
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${designTokens.colors.gray[300]};
    border-radius: 999px;

    &:hover {
      background: ${designTokens.colors.gray[400]};
    }
  }

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

// Modal Footer
export const ModalFooter = styled.div`
  padding: 16px 24px 24px 24px;
  border-top: 1px solid ${designTokens.colors.gray[200]};
  display: flex;
  align-items: center;
  justify-content: ${props => props.align === 'left' ? 'flex-start' : props.align === 'center' ? 'center' : 'flex-end'};
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 12px 16px 20px 16px;
    flex-direction: ${props => props.stackOnMobile ? 'column-reverse' : 'row'};

    ${props => props.stackOnMobile && `
      button {
        width: 100%;
      }
    `}
  }
`;

// Modal Actions (for buttons in footer)
export const ModalActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: ${props => props.stackOnMobile ? 'column-reverse' : 'row'};

    ${props => props.stackOnMobile && `
      button {
        width: 100%;
      }
    `}
  }
`;

// Modal Description (optional subtitle in header)
export const ModalDescription = styled.p`
  font-family: ${designTokens.fonts.body};
  font-size: 0.875rem;
  color: ${designTokens.colors.gray[600]};
  margin: 4px 0 0 0;
  line-height: 1.5;
`;
